export default {
    title: 'Habits',
    subtitle: 'Today {done} of {total}',
    newHabit: 'New habit',
    today: {
        title: 'Today',
        nothing: 'No habits are due today.',
    },
    other: {
        title: 'Not today',
    },
    empty: {
        title: 'No habits yet',
        text: 'Add your first one — water, reading or a walk — and tick it off every day.',
    },
    archive: {
        title: 'Archive',
        empty: 'Nothing here.',
    },
    pick: {
        title: 'Pick a habit',
        text: 'You’ll see its streak, success rate and the last weeks — and can change what you need.',
    },
    toolbar: {
        back: 'Back to the list',
        archived: 'Archived',
        archive: 'Archive',
        restore: 'Restore',
        remove: 'Delete habit',
    },
    toast: {
        saved: 'Saved.',
        archived: '"{name}" archived.',
        restored: '"{name}" is back on the list.',
        removed: 'Deleted "{name}".',
    },
    goal: 'goal: {target} {unit}',
    notToday: 'not due today',
    week: 'this week {done} of {times}',
    control: {
        check: 'Tick off: {name}',
        undo: 'Untick: {name}',
        minus: 'Subtract 1: {name}',
        plus: 'Add 1: {name}',
    },
    numbers: {
        title: 'How it’s going',
        streak: 'Streak',
        best: 'Longest streak',
        rate: '30-day success',
    },
    streak: {
        days: '{n} day | {n} days | {n} days',
        weeks: '{n} week | {n} weeks | {n} weeks',
    },
    grid: {
        title: 'Last 16 weeks',
        states: {
            done: 'done',
            partial: 'partly',
            missed: 'missed',
            today: 'today, not yet',
            off: 'not due',
            before: 'before the start',
            future: 'still ahead',
        },
        legend: {
            done: 'done',
            partial: 'partly',
            missed: 'missed',
            off: 'not due',
        },
    },
    edit: {
        title: 'Habit settings',
    },
    kinds: {
        check: 'Yes/no',
        count: 'With a goal',
    },
    schedule: {
        daily: 'Every day',
        weekdays: 'Chosen days',
        times_per_week: 'A few times a week',
        dailyLabel: 'every day',
        timesLabel: '{n} time a week | {n} times a week | {n} times a week',
    },
    form: {
        suggestions: 'Suggestions',
        icon: 'Emoji',
        iconHint: 'Emoji next to the name (optional)',
        name: 'Name',
        namePlaceholder: 'E.g. Water, Reading, Walk…',
        kind: 'Kind',
        targetPrefix: 'Daily goal:',
        target: 'Daily goal',
        unit: 'Unit',
        unitPlaceholder: 'e.g. glasses',
        schedule: 'Rhythm',
        times: 'Times a week',
        timesSuffix: 'time a week | times a week | times a week',
        reminder: 'Reminder',
        noReminder: 'None',
        remindAt: 'At',
        remindTime: 'Reminder time',
        reminderHint: 'The notification comes only when the habit is due today and not ticked off yet (turn them on in Planner → Settings).',
        save: 'Save changes',
        add: 'Add habit',
        cancel: 'Cancel',
    },
    suggestions: {
        water: { label: 'Water — 8 glasses', name: 'Water', unit: 'glasses' },
        reading: { label: 'Reading — 20 pages', name: 'Reading', unit: 'pages' },
        stretching: { label: 'Stretching', name: 'Stretching' },
        sweets: { label: 'No sweets', name: 'No sweets' },
        walk: { label: 'Walk', name: 'Walk' },
        meditation: { label: 'Meditation', name: 'Meditation' },
    },
    planner: {
        title: 'Habits of the day',
        open: 'Open Habits',
    },
    help: {
        today: {
            title: 'Today',
            text: 'Habits due today: tap the circle to tick one off, or −/+ for a daily goal. 🔥 is the streak.',
            more: 'A day counts when you tick off a yes/no habit or reach the goal (e.g. 8 of 8 glasses). A missed day doesn’t become overdue — it only breaks the streak. Today, not ticked off yet, doesn’t break it.',
        },
        other: {
            title: 'Not today',
            text: 'Habits whose rhythm doesn’t need today — e.g. only on Mondays, or already done 3 times this week.',
            more: 'You can tick them off anyway — it counts towards the history and, for "N times a week", the week.',
        },
        add: {
            title: 'New habit',
            text: 'A name, a kind (yes/no or with a goal), a rhythm and — if you like — a reminder time.',
            more: 'The assistant adds a habit too when you ask for it: "I want to drink 8 glasses of water a day". And "I drank 2 glasses of water" ticks it off for you.',
        },
        grid: {
            title: 'Last weeks',
            text: 'Each square is a day: green — done, lighter — part of the goal, grey — missed, empty — not due.',
            more: 'Tap a day to fill it in afterwards (adds 1 for a goal, toggles a yes/no habit).',
        },
        numbers: {
            title: 'How it’s going',
            text: 'Streak — due days (or weeks) in a row. Longest — the record. Success — how many of the due days in the last 30 you managed.',
            more: 'With "N times a week" weeks count, and the current week in progress doesn’t break the streak.',
        },
        schedule: {
            title: 'Rhythm',
            text: 'Every day, on chosen days of the week, or N times a week on any days.',
            more: 'On days outside the rhythm the habit isn’t due and doesn’t break the streak.',
        },
    },
    tour: {
        welcome: {
            title: 'Welcome to Habits',
            description: 'Things you want to do regularly — you tick them off every day anew, and the streak grows.',
        },
        habit: {
            title: 'A habit',
            description: 'Tap a habit to see its streak, longest streak, success rate, the grid of the last weeks and its settings. Archiving and deleting are there too.',
        },
        end: {
            title: 'That’s it',
            description: 'The guide is always under "Help" — the First steps are there too. You also see the day’s habits in the Planner.',
        },
    },
    firstSteps: {
        habit: {
            title: 'First habit',
            action: 'Add a habit',
            what: 'Tap "New habit" and pick a suggestion or type your own.',
            why: 'That’s where the streak starts — the habit shows up in "Today" and in the Planner.',
        },
        log: {
            title: 'First tick',
            action: 'Tick off',
            what: 'Tap the circle next to the habit (or + for a goal).',
            why: 'Every day done makes the streak longer.',
        },
        count: {
            title: 'A habit with a goal',
            action: 'Add water',
            what: 'Add a habit with a goal, e.g. "Water — 8 glasses", and add one at a time.',
            why: 'You see "3/8" and know how much is left for today.',
        },
        streak3: {
            title: 'Streak of 3',
            action: 'See the streak',
            what: 'Complete the habit on three due days in a row.',
            why: 'Three days is the first milestone — it gets easier from there.',
        },
    },
};
